<template>
  <Popup
    v-model:show="visible"
    teleport="body"
    position="bottom"
    :style="{
      height: 'calc(100% - 8px)'
    }"
  >
    <div class="h5-recently-viewed">
      <div class="h5-recently-viewed__header">
        <Icon
          class="h5-recently-viewed__close"
          name="close-l"
          @click="visible = false"
        />
        <div class="h5-recently-viewed__title">
          Recently Viewed
        </div>
        <div
          v-if="history.list.length > 0"
          class="h5-recently-viewed__clear"
          @click.stop="clearHistory"
        >
          Clear Viewed
        </div>
      </div>
      <div
        class="h5-recently-viewed__list"
        v-if="history.list.length > 0"
      >
        <div
          v-for="item in history.list"
          :key="item.offerId"
          class="h5-recently-viewed-item"
          @click="onItemClick(item)"
        >
          <Image
            class="h5-recently-viewed-item__image"
            :src="item.imageUrl"
            alt=""
          />
          <div class="h5-recently-viewed-item__content">
            <div class="h5-recently-viewed-item__title">
              {{ item.subjectTrans }}
            </div>
            <div class="h5-recently-viewed-item__price">
              <IntlCurrency :currency="currency.current.targetCurrency" />
              {{ productPrice(item) }}
            </div>
          </div>
        </div>
      </div>
      <Result
        v-else
        class="h5-recently-viewed-empty"
        :image="EmptyImage"
        title="We haven’t found any items here."
      />
    </div>
  </Popup>
</template>

<script setup>
import { useHistoryStore } from '@/pinia/history'
import { Image } from '@/components/Image'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { Popup } from '@/components/Popup'
import { bankersRoundWithMax, buriedPointUtil, openURLWithChannelQuery } from '@/utils/utils'
import { useCurrencyStore } from '@/pinia/currency'
import { Icon } from '@/components/Icon'
import { Result } from '@/components/Result'
import EmptyImage from '@/assets/result/empty.svg'

const visible = defineModel('show', { type: Boolean })

const history = useHistoryStore()

const currency = useCurrencyStore()

const productPrice = computed(() => (item) => {
  if (item) {
    const price = item?.priceInfo?.price || 0
    return bankersRoundWithMax(price * currency.current.rate, 0.01)
  } else {
    return ''
  }
})

watch(visible, (value) => {
  if (value) {
    buriedPointUtil({ eventId: 'HRV101' })
  }
})

const clearHistory = () => {
  buriedPointUtil({
    eventId: 'HRV202'
  })
  history.clear()
}

const onItemClick = (item) => {
  buriedPointUtil({
    eventId: 'HRV201',
    others: {
      offerId: item.offerId
    }
  })
  openURLWithChannelQuery(`/offer/${item.offerId}?key=${btoa(item.monthSold)}`)
}
</script>

<style lang="less" scoped>
.h5-recently-viewed {
  display: flex;
  flex-direction: column;
  border-radius: 16px 16px 0 0;
  background-color: #FFFFFF;
  height: 100%;

  &__header {
    flex-shrink: 0;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px 12px;
  }

  &__close {
    position: absolute;
    top: 20px;
    left: 12px;
    font-size: 20px;
  }

  &__title {
    color: #232323;
    font-size: 14px;
    font-family: mon-700;
  }

  &__clear {
    position: absolute;
    top: 24px;
    right: 12px;
    color: #5c5c5c;
    font-size: 11px;
    font-family: mon-500;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
      color: #004F9A;
    }
  }

  &__list {
    flex: 1;
    overflow-y: scroll;
    padding: 0 8px 16px;
  }

  &-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;

    &:hover {
      background-color: #f8f8f8;
      border-radius: 6px;
      cursor: pointer;
    }

    &__image {
      flex-shrink: 0;
      width: 72px;
      height: 72px;
      border-radius: 4px;
      border: 1px solid #EBF1F5;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    &__title {
      color: #232323;
      font-size: 12px;
      font-family: mon-500;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    &__price {
      display: flex;
      align-items: center;
      color: #232323;
      font-size: 12px;
      line-height: 16px;
      font-family: mon-700;
    }
  }

  &-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
}
</style>
